import type { InstanceId, ItemId, RoomId, SetId, ThemeTag } from '@/core/content/ids';
import type { ContentRegistry } from '@/core/content/registry';
import type { DomainEvent } from '@/core/events/types';
import type { GameSave, SlotContent } from '@/core/persistence/schema/v1';
import type { HandlerEnv } from './placement/handlers';
import { slotParent, type SlotKey } from './placement/geometry';
import { isRightSpot, type PlacementTarget } from './placement/rules';
import { grantRewards } from './rewards';

export interface CozinessBreakdown {
  total: number;
  rightSpotIds: InstanceId[];
  completeSets: SetId[];
  themes: ThemeTag[];
}

/** The target an already-placed instance occupies (for right-spot checks). */
function currentTarget(state: GameSave, roomId: RoomId, instanceId: InstanceId): PlacementTarget | undefined {
  const room = state.rooms[roomId];
  const p = room.placements[instanceId];
  if (p) {
    return p.layer === 'wall'
      ? { layer: 'wall', roomId, wall: p.wall, offset: p.offset }
      : { layer: p.layer, roomId, x: p.x, y: p.y, rotation: p.rotation };
  }
  for (const [key, c] of Object.entries(room.slots) as [SlotKey, SlotContent][]) {
    if (c.kind === 'instance' && c.instanceId === instanceId) {
      return {
        layer: 'surface',
        roomId,
        parentInstanceId: slotParent(key),
        slotIndex: Number(key.slice(key.indexOf('#') + 1)),
      };
    }
  }
  return undefined;
}

/** Points for a room (content-schemas §14.1). Pure and deterministic. */
export function computeCoziness(
  content: ContentRegistry,
  state: GameSave,
  roomId: RoomId,
): CozinessBreakdown {
  const W = content.balance.cozinessWeights;
  const room = state.rooms[roomId];
  const placed = [
    ...Object.keys(room.placements),
    ...Object.values(room.slots).flatMap((c) => (c.kind === 'instance' ? [c.instanceId] : [])),
  ].sort() as InstanceId[];
  const copies = new Map<ItemId, number>();
  const present = new Set<ItemId>();
  const themes = new Set<ThemeTag>();
  const rightSpotIds: InstanceId[] = [];
  let base = 0;
  let right = 0;
  for (const id of placed) {
    const def = content.items.get(state.inventory.instances[id]?.itemId ?? 'dec_none');
    if (!def) continue;
    present.add(def.id);
    const n = (copies.get(def.id) ?? 0) + 1;
    copies.set(def.id, n);
    if (def.kind === 'fixture') continue;
    const counted = n <= W.maxCountedCopiesPerItem;
    if (counted) base += def.kind === 'furniture' ? W.furniture : W.decor;
    const target = currentTarget(state, roomId, id);
    if (counted && target && isRightSpot(content, state, id, target)) {
      right += W.rightSpot;
      rightSpotIds.push(id);
    }
    for (const t of def.themeTags) themes.add(t);
  }
  const units = Object.values(room.slots).filter((c) => c.kind === 'product').length;
  base += Math.min(units * W.product, W.productCap);
  const diversity = Math.min(themes.size * W.themeDiversity, W.themeDiversityCap);
  const completeSets = [...content.sets.values()]
    .filter((set) => set.itemIds.every((itemId) => present.has(itemId)))
    .map((s) => s.id);
  return {
    total: base + right + diversity + completeSets.length * W.setBonus,
    rightSpotIds,
    completeSets,
    themes: [...themes],
  };
}

/** Highest level whose threshold ≤ points (0 when below level 1). */
export function cozinessLevelFor(content: ContentRegistry, roomId: RoomId, points: number): number {
  return content.balance.cozinessLevels[roomId].filter((t) => t <= points).length;
}

const ROOM_EVENTS = new Set<DomainEvent['type']>([
  'ITEM_PLACED',
  'ITEM_ROTATED',
  'ITEM_STORED',
  'PRODUCT_DISPLAYED',
  'PRODUCT_UNDISPLAYED',
]);

/**
 * Domain system (dispatch step 4b): recompute touched rooms, raise `best`, pay each newly reached level once
 * and celebrate first-time complete sets. Levels are based on `best`, so rearranging never loses rewards.
 */
export function cozinessSystem(
  draft: GameSave,
  events: readonly DomainEvent[],
  env: HandlerEnv,
): DomainEvent[] {
  const rooms = new Set<RoomId>();
  for (const e of events) if (ROOM_EVENTS.has(e.type) && 'roomId' in e) rooms.add(e.roomId);
  return refreshCoziness(draft, [...rooms], env);
}

/** Recompute rooms and pay any newly reached levels/sets (also used on load to reconcile, §11.6). */
export function refreshCoziness(draft: GameSave, rooms: readonly RoomId[], env: HandlerEnv): DomainEvent[] {
  const out: DomainEvent[] = [];
  for (const roomId of rooms) {
    const r = computeCoziness(env.content, draft, roomId);
    const c = draft.coziness[roomId];
    if (r.total !== c.current) {
      c.current = r.total;
      c.best = Math.max(c.best, r.total);
      out.push({ type: 'COZINESS_CHANGED', roomId, current: c.current, best: c.best });
    }
    const level = cozinessLevelFor(env.content, roomId, c.best);
    for (let L = c.bestLevel + 1; L <= level; L++) {
      const rewards = env.content.cozinessRewards[roomId][L - 1] ?? [];
      out.push({ type: 'COZINESS_LEVEL_REACHED', roomId, level: L, rewards: [...rewards] });
      out.push(...grantRewards(draft, env.content, `coz:${roomId}:L${String(L)}`, rewards));
    }
    c.bestLevel = Math.max(c.bestLevel, level);
    const done = new Set(draft.stats.distinct.setsCompleted ?? []);
    for (const setId of r.completeSets) {
      if (done.has(setId)) continue;
      const set = env.content.sets.get(setId);
      const rewards = set ? [...set.completionRewards] : [];
      draft.stats.distinct.setsCompleted = [...done, setId];
      done.add(setId);
      out.push({ type: 'SET_COMPLETED', setId, roomId, rewards });
      out.push(...grantRewards(draft, env.content, `set:${setId}`, rewards));
    }
  }
  return out;
}
